import {
  GoogleCoordinates,
  HttpResponse
} from '../interfaces/googleMapsInterfaces'
import { presenter } from '@wiggot/core-apis/src/presenter/index'

/**
 * This service is used to get the predictions of a place.
 * @param {AutocompletionRequest} params parameters to get the predictions of a pl
 * @returns {Promise<HttpResponse<any[]>>} returns a promise with the response of the service
 */
export const getGoogleMapsPredictions = async (
  params: google.maps.places.AutocompletionRequest
): Promise<HttpResponse<any[]>> => {
  try {
    const response =
      await new window.google.maps.places.AutocompleteService().getPlacePredictions(
        params
      )

    if (!response?.predictions?.length) {
      return {
        isSuccess: false,
        error: {
          message: 'No se encontraron las predicciones',
          code: 'no_predictions_found'
        }
      }
    }

    const formatted = presenter<any[]>({
      value: response?.predictions,
      style: 'camel'
    })

    return {
      isSuccess: true,
      data: formatted
    }
  } catch (e) {
    return {
      isSuccess: false,
      error: {
        message: 'Error al obtener las predicciones',
        code: 'error_getting_predictions'
      }
    }
  }
}

/**
 * This service is used to get the details of a place.
 * @param {string} params placeId of the place to get the details
 * @returns {Promise<HttpResponse<any>>} returns a promise with the response of the service
 */
export const getPlaceDetail = async (params: {
  placeId: string
}): Promise<HttpResponse<any>> => {
  const { placeId } = params

  const service = new google.maps.places.PlacesService(
    window.document.createElement('div')
  )
  return new Promise((resolve, reject) => {
    service.getDetails(
      {
        placeId,
        fields: [
          'address_components',
          'formatted_address',
          'place_id',
          'types',
          'geometry'
        ]
      },
      (responses, status: string) => {
        if (status === 'OK') {
          const formatted = presenter<any>({
            value: responses,
            style: 'camel',
            whiteList: ['utc_offset']
          })

          resolve({
            isSuccess: true,
            data: formatted
          })
        }

        // eslint-disable-next-line prefer-promise-reject-errors
        reject({
          isSuccess: false,
          error: {
            message: 'Error al obtener el detalle del lugar',
            code: 'error_getting_place_detail'
          }
        })
      }
    )
  })
}

/**
 * This service is used to get the coordinates of a place.
 * @param {string} params placeId of the place to get the coordinates
 * @returns {Promise<HttpResponse<any>>} returns a promise with the response of the service
 */
export const getPlaceFromGeocoding = async (params: {
  placeId: string
}): Promise<HttpResponse<any>> => {
  const { placeId } = params

  const geocoder = new google.maps.Geocoder()

  return new Promise((resolve, reject) => {
    geocoder.geocode(
      {
        placeId
      },
      (responses, status) => {
        if (status === 'OK') {
          const formatted = presenter<any>({
            value: responses?.length ? responses[0] : {},
            style: 'camel',
            whiteList: ['utc_offset']
          })

          resolve({
            isSuccess: true,
            data: formatted
          })
        }

        // eslint-disable-next-line prefer-promise-reject-errors
        reject({
          isSuccess: false,
          error: {
            message: 'Error al obtener el detalle del lugar',
            code: 'error_getting_place_detail'
          }
        })
      }
    )
  })
}

export const getPlacesByGeocode = async (params: {
  coordinates: GoogleCoordinates
}): Promise<
  HttpResponse<
    {
      addressComponents: {
        longName: string
        shortName: string
        types: string[]
      }[]
      formattedAddress: string
      geometry: {
        location: {
          lat: () => number
          lng: () => number
        }
      }
      placeId: string
      types: string[]
    }[]
  >
> => {
  const {
    coordinates: { latitude, longitude }
  } = params

  const geocoder = new google.maps.Geocoder()

  return new Promise((resolve, reject) => {
    geocoder.geocode(
      {
        location: {
          lat: latitude,
          lng: longitude
        }
      },
      (responses: any, status: string) => {
        if (status === 'OK') {
          const formatted = presenter<any[]>({
            value: responses,
            style: 'camel'
          })

          resolve({
            isSuccess: true,
            data: formatted
          })
        }

        // eslint-disable-next-line prefer-promise-reject-errors
        reject({
          isSuccess: false,
          error: {
            message: 'Error al obtener las coincidencias',
            code: 'error_getting_coincidences'
          }
        })
      }
    )
  })
}
